import { iframeDataBridge } from './iframeDataBridge';
import { log } from '../utils/vueLogger';

/**
 * Bidirectional postMessage relay between the Back-Office and the legacy m.eagle
 * iframe nested inside this Vue shell.
 *
 * Frameset layout: a top shell frameset holds two SIBLING frames — `frameWA`
 * (this Vue app, which itself nests m.eagle) and `frameBO` (the Back-Office).
 * The BO's message listener (runtimeHandleMessage) lives on `frameBO`, and the
 * BO posts to `frameWA.contentWindow` (this window). So from here the BO is a
 * *sibling* — NOT window.parent (which is the frameset shell).
 *
 * Context: the BO used to load m.eagle directly as `frameWA`, so its postMessages
 * reached comm.js in a single hop. Now `frameWA` is this Vue app and m.eagle is a
 * *nested* iframe (see WebAppIframe.vue), and postMessage does not cross the extra
 * layer. Without this relay:
 *   - the BO's `hello`/`visit`/`assortpdv` never reach comm.js, and
 *   - comm.js's `webAppReady`/`webAppNeedReload` never reach the BO, so the BO
 *     never flips the `ecos.webapp.status` cookie to `ready` and its
 *     `_waShowAppointment` loop spins forever.
 *
 * SECURITY: comm.js trusts our (same) origin (see its origin guard), so THIS relay
 * is the trust boundary for downstream traffic — it forwards a message to the
 * legacy iframe only when it genuinely comes from an allowed Back-Office origin
 * (and never from our own origin, i.e. ourselves or the legacy child).
 */

// Actions the BO sends down to the legacy iframe.
const DOWNSTREAM_ACTIONS = ['hello', 'visit', 'assortpdv'];
// Signals comm.js emits up to the BO.
const UPSTREAM_ACTIONS = ['webAppReady', 'webAppNeedReload'];

// Mirrors comm.js's allow-list of Back-Office origins.
const BO_ORIGINS = ['https://bo-dev.ecos.externis.net', 'https://bo.ecos.externis.net', 'https://bo.ecos2.externis.com', 'https://localhost:50203'];

function isBoOrigin(origin) {
	return BO_ORIGINS.includes(origin) || origin.indexOf('localhost') >= 0;
}

// Best-effort BO origin for the upstream direction when the BO has not sent
// anything yet (webAppReady fires before the first visit). Mirrors the m.* ↔ bo.*
// host convention (m.ecos.externis.net → bo.ecos.externis.net, etc.).
function deriveBoOrigin() {
	const { protocol, hostname } = window.location;
	if (/^m[.-]/.test(hostname)) {
		return `${protocol}//${hostname.replace(/^m([.-])/, 'bo$1')}`;
	}
	return null;
}

// The BO window that talks to us, captured from its first message (a sibling frame).
let observedBoWindow = null;
let observedBoOrigin = null;
// Downstream messages that arrived before the legacy iframe could receive them.
const pendingDownstream = [];

function postDownstream(message) {
	const win = iframeDataBridge.getIframeWindow();
	if (win) {
		win.postMessage(message, window.location.origin);
		return;
	}
	pendingDownstream.push(message);
}

function flushDownstream() {
	const win = iframeDataBridge.getIframeWindow();
	if (!win) return;
	while (pendingDownstream.length) {
		win.postMessage(pendingDownstream.shift(), window.location.origin);
	}
}

// Candidate BO windows for upstream signals. The BO listener lives on exactly one
// of these depending on the frameset layout (sibling frameBO vs the top shell);
// posting to all is harmless — the others ignore the action. We add every sibling
// frame by index because named access (frames['frameBO']) is blocked cross-origin.
function boTargets() {
	const targets = new Set();
	if (observedBoWindow) targets.add(observedBoWindow);
	try {
		const top = window.top;
		if (top) {
			for (let i = 0; i < top.frames.length; i++) {
				const frame = top.frames[i];
				if (frame && frame !== window) targets.add(frame);
			}
			if (top !== window) targets.add(top);
		}
		if (window.parent && window.parent !== window) targets.add(window.parent);
	} catch {
		if (window.top && window.top !== window) targets.add(window.top);
	}
	return targets;
}

function relayUpstream(message) {
	const origin = observedBoOrigin || deriveBoOrigin() || '*';
	const targets = boTargets();
	targets.forEach((win) => win.postMessage(message, origin));
	log('info', 'boMessageRelay', `↑ relayed '${message.action}' to ${targets.size} BO target(s) @ ${origin}`);
}

export function initBoMessageRelay() {
	window.addEventListener('message', (event) => {
		const action = event.data?.action;
		if (!action) return;

		// Downstream: BO (a sibling frame) → legacy iframe.
		if (DOWNSTREAM_ACTIONS.includes(action)) {
			// Never trust our own origin (that would be us or the legacy child).
			if (event.origin === window.location.origin) return;
			if (!isBoOrigin(event.origin)) return;

			observedBoWindow = event.source || observedBoWindow;
			observedBoOrigin = event.origin;
			postDownstream(event.data);
			log('info', 'boMessageRelay', `↓ relayed '${action}' from BO (${event.origin}) to legacy iframe`);
			return;
		}

		// Upstream: legacy iframe → BO.
		if (UPSTREAM_ACTIONS.includes(action)) {
			if (event.source !== iframeDataBridge.getIframeWindow()) return;
			if (event.origin !== window.location.origin) return;
			relayUpstream(event.data);
		}
	});

	// Re-arm comm.js's `frameParent` on every (re)load of the legacy iframe:
	// comm.js only records its parent when it receives a `hello`, and loses that
	// reference on reload (c_frameMustReload). Sending our own hello makes the
	// ready handshake independent of whether/when the BO sends one, and routes
	// webAppReady back through us (→ out to the BO frames).
	const armHello = () => {
		flushDownstream();
		postDownstream({ action: 'hello' });
		log('info', 'boMessageRelay', 'sent hello to legacy iframe (arm frameParent)');
	};

	const iframe = iframeDataBridge.getIframe();
	iframe?.addEventListener('load', armHello);
	// Cover the case where the iframe already finished loading before init ran.
	armHello();
}
